import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import usePermissions from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../config/permissions';

const TABS = ['Events', 'Attendances'];

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  upcoming: 'bg-blue-100 text-blue-700',
  ongoing: 'bg-green-100 text-green-700',
  completed: 'bg-purple-100 text-purple-700',
  cancelled: 'bg-red-100 text-red-700',
};

const CATEGORY_OPTIONS = [
  { value: 'conference', label: 'Conference' },
  { value: 'seminar', label: 'Seminar' },
  { value: 'workshop', label: 'Workshop' },
  { value: 'webinar', label: 'Webinar' },
  { value: 'social', label: 'Social Event' },
  { value: 'fundraiser', label: 'Fundraiser' },
  { value: 'award', label: 'Award Ceremony' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

function FormInput({ label, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600 mb-1">{label}</label>
      <input {...props} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
    </div>
  );
}

function FormSelect({ label, options, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600 mb-1">{label}</label>
      <select {...props} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

export default function AdminEvents() {
  const [tab, setTab] = useState('Events');
  const [stats, setStats] = useState(null);

  const fetchStats = useCallback(() => {
    api.get('/admin/events/stats/').then(r => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Events Management</h1>
      <p className="text-sm text-gray-500 mb-6">Manage events, registrations, and attendance</p>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total Events" value={stats.total} color="text-gray-900" />
          <StatCard label="Upcoming" value={stats.upcoming} color="text-blue-600" />
          <StatCard label="Ongoing" value={stats.ongoing} color="text-green-600" />
          <StatCard label="Registered" value={stats.total_attendances} color="text-purple-600" />
        </div>
      )}

      <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 w-fit">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition ${
              tab === t ? 'bg-white text-[#0B5D1E] shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>{t}</button>
        ))}
      </div>

      {tab === 'Events' && <EventsTab onRefreshStats={fetchStats} />}
      {tab === 'Attendances' && <AttendancesTab />}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border p-4">
      <p className="text-xs text-gray-500 font-semibold uppercase">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// ═══ EVENTS TAB ═══
// ══════════════════════════════════════════════════════════════

function EventsTab({ onRefreshStats }) {
  const { canEdit, canAdd, canDelete } = usePermissions();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [form, setForm] = useState({
    title: '', description: '', category: 'other', venue: '',
    is_virtual: false, meeting_link: '', date_from: '', date_to: '',
    time_start: '', time_end: '', max_participants: '', fee: '0',
    status: 'draft',
  });
  const [coverImage, setCoverImage] = useState(null);

  const fetchEvents = useCallback(() => {
    setLoading(true);
    let url = '/admin/events/?';
    if (search) url += `search=${encodeURIComponent(search)}&`;
    if (statusFilter) url += `status=${statusFilter}&`;
    api.get(url)
      .then(r => setEvents(r.data.results || r.data))
      .catch(() => toast.error('Failed to load events'))
      .finally(() => setLoading(false));
  }, [search, statusFilter]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const resetForm = () => {
    setForm({
      title: '', description: '', category: 'other', venue: '',
      is_virtual: false, meeting_link: '', date_from: '', date_to: '',
      time_start: '', time_end: '', max_participants: '', fee: '0',
      status: 'draft',
    });
    setCoverImage(null);
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (ev) => {
    setForm({
      title: ev.title, description: ev.description || '', category: ev.category,
      venue: ev.venue || '', is_virtual: ev.is_virtual,
      meeting_link: ev.meeting_link || '', date_from: ev.date_from,
      date_to: ev.date_to, time_start: ev.time_start || '',
      time_end: ev.time_end || '', max_participants: ev.max_participants || '',
      fee: ev.fee || '0', status: ev.status,
    });
    setCoverImage(null);
    setEditing(ev);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (v !== '' && v !== null) fd.append(k, v);
    });
    if (coverImage) fd.append('cover_image', coverImage);

    try {
      if (editing) {
        await api.patch(`/admin/events/${editing.id}/`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Event updated');
      } else {
        await api.post('/admin/events/', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Event created');
      }
      resetForm();
      fetchEvents();
      onRefreshStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save event');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await api.delete(`/admin/events/${id}/`);
      toast.success('Event deleted');
      fetchEvents();
      onRefreshStats();
    } catch { toast.error('Failed to delete event'); }
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <input type="text" placeholder="Search events..." value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] outline-none">
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {canAdd(PERMISSIONS.ADD_EVENTS) && (
          <button onClick={() => { resetForm(); setShowForm(!showForm); }}
            className="bg-[#0B5D1E] text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition whitespace-nowrap">
            {showForm ? 'Cancel' : '+ New Event'}
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border p-5 mb-5">
          <h3 className="font-semibold text-gray-700 mb-4">{editing ? 'Edit Event' : 'Create New Event'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <FormInput label="Title" required value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
            </div>
            <FormSelect label="Category" options={CATEGORY_OPTIONS} value={form.category} onChange={e => setForm({...form, category: e.target.value})} />
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
              <textarea rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
            </div>
            <FormInput label="Venue" value={form.venue} onChange={e => setForm({...form, venue: e.target.value})} />
            <FormInput label="Date From" type="date" required value={form.date_from} onChange={e => setForm({...form, date_from: e.target.value})} />
            <FormInput label="Date To" type="date" required value={form.date_to} onChange={e => setForm({...form, date_to: e.target.value})} />
            <FormInput label="Time Start" type="time" value={form.time_start} onChange={e => setForm({...form, time_start: e.target.value})} />
            <FormInput label="Time End" type="time" value={form.time_end} onChange={e => setForm({...form, time_end: e.target.value})} />
            <FormInput label="Max Participants" type="number" value={form.max_participants} onChange={e => setForm({...form, max_participants: e.target.value})} placeholder="Leave empty for unlimited" />
            <FormInput label="Fee (₦)" type="number" step="0.01" value={form.fee} onChange={e => setForm({...form, fee: e.target.value})} />
            <FormSelect label="Status" options={STATUS_OPTIONS} value={form.status} onChange={e => setForm({...form, status: e.target.value})} />
            <div className="flex items-end gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={form.is_virtual} onChange={e => setForm({...form, is_virtual: e.target.checked})}
                  className="rounded border-gray-300 text-[#0B5D1E] focus:ring-[#0B5D1E]" />
                Virtual Event
              </label>
            </div>
            {form.is_virtual && (
              <div className="md:col-span-2">
                <FormInput label="Meeting Link" type="url" value={form.meeting_link} onChange={e => setForm({...form, meeting_link: e.target.value})} />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Cover Image</label>
              <input type="file" accept="image/*" onChange={e => setCoverImage(e.target.files[0])}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:bg-[#0B5D1E]/10 file:text-[#0B5D1E]" />
            </div>
          </div>
          <div className="flex gap-3 mt-5">
            <button type="submit" className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition">
              {editing ? 'Update Event' : 'Create Event'}
            </button>
            <button type="button" onClick={resetForm} className="px-6 py-2 rounded-lg text-sm font-semibold text-gray-500 hover:text-gray-700 border hover:border-gray-300 transition">
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Events List */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : events.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <p className="text-gray-400">No events found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map((ev, idx) => (
            <div key={ev.id} className="bg-white rounded-xl shadow-sm border p-4 flex items-start gap-4">
              <span className="bg-[#0B5D1E] text-white text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1">{idx + 1}</span>
              {ev.cover_image && (
                <img src={ev.cover_image} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-gray-800 truncate">{ev.title}</h3>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[ev.status] || ''}`}>
                    {ev.status_display || ev.status}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                    {ev.category_display || ev.category}
                  </span>
                </div>
                <div className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                  <span>{ev.date_from} → {ev.date_to}</span>
                  {ev.venue && <span>📍 {ev.venue}</span>}
                  <span>{ev.registered_count || 0} registered</span>
                  {ev.fee > 0 && <span>₦{Number(ev.fee).toLocaleString()}</span>}
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                {canEdit(PERMISSIONS.EDIT_EVENTS) && (
                  <button onClick={() => handleEdit(ev)}
                    className="p-2 text-gray-400 hover:text-[#0B5D1E] transition" title="Edit">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                )}
                {canDelete(PERMISSIONS.DELETE_EVENTS) && (
                  <button onClick={() => handleDelete(ev.id)}
                    className="p-2 text-gray-400 hover:text-red-500 transition" title="Delete">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// ═══ ATTENDANCES TAB ═══
// ══════════════════════════════════════════════════════════════

function AttendancesTab() {
  const { canEdit, canAdd } = usePermissions();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [attendances, setAttendances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [bulkRegistering, setBulkRegistering] = useState(false);
  const [showSingleForm, setShowSingleForm] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [singleRegistering, setSingleRegistering] = useState(false);

  useEffect(() => {
    api.get('/admin/events/')
      .then(r => setEvents(r.data.results || r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedEvent) { setAttendances([]); return; }
    setLoading(true);
    let url = `/admin/events/${selectedEvent}/attendances/?`;
    if (search) url += `search=${encodeURIComponent(search)}&`;
    api.get(url)
      .then(r => setAttendances(r.data.results || r.data))
      .catch(() => toast.error('Failed to load attendances'))
      .finally(() => setLoading(false));
  }, [selectedEvent, search]);

  // Search members for single registration
  useEffect(() => {
    if (memberSearch.length < 2) { setMemberResults([]); return; }
    const timer = setTimeout(() => {
      api.get(`/accounts/admin/members/?search=${encodeURIComponent(memberSearch)}&page_size=10`)
        .then(r => setMemberResults(r.data.results || r.data))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [memberSearch]);

  const handleAction = async (attId, action) => {
    try {
      const res = await api.post(`/admin/event-attendances/${attId}/action/`, { action });
      setAttendances(prev => prev.map(a => a.id === attId ? res.data : a));
      toast.success(action === 'mark_attendance' ? 'Attendance marked' : action === 'unmark_attendance' ? 'Attendance unmarked' : 'Certificate issued');
    } catch {
      toast.error('Action failed');
    }
  };

  const handleBulkRegister = async () => {
    if (!selectedEvent) return;
    if (!window.confirm('Register all active members for this event?')) return;
    setBulkRegistering(true);
    try {
      const res = await api.post(`/admin/events/${selectedEvent}/bulk-register/`, {});
      toast.success(res.data.detail);
      // Refresh attendances
      const att = await api.get(`/admin/events/${selectedEvent}/attendances/`);
      setAttendances(att.data.results || att.data);
    } catch {
      toast.error('Bulk registration failed');
    }
    setBulkRegistering(false);
  };

  const handleSingleRegister = async () => {
    if (!selectedEvent || !selectedMember) return;
    setSingleRegistering(true);
    try {
      await api.post(`/admin/events/${selectedEvent}/single-register/`, { member_id: selectedMember.id });
      toast.success(`${selectedMember.full_name || selectedMember.user_full_name} registered successfully`);
      setSelectedMember(null);
      setMemberSearch('');
      setShowSingleForm(false);
      // Refresh attendances
      const att = await api.get(`/admin/events/${selectedEvent}/attendances/`);
      setAttendances(att.data.results || att.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    }
    setSingleRegistering(false);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <select value={selectedEvent} onChange={e => setSelectedEvent(e.target.value)}
          className="flex-1 px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] outline-none">
          <option value="">-- Select Event --</option>
          {events.map(ev => (
            <option key={ev.id} value={ev.id}>{ev.title} ({ev.status})</option>
          ))}
        </select>
        <input type="text" placeholder="Search attendees..." value={search}
          onChange={e => setSearch(e.target.value)}
          className="px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
        {selectedEvent && (
          <div className="flex gap-2">
            {canAdd(PERMISSIONS.ADD_EVENTS) && (
              <button onClick={() => setShowSingleForm(!showSingleForm)}
                className="bg-[#0B5D1E] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition whitespace-nowrap">
                {showSingleForm ? 'Cancel' : '+ Register Member'}
              </button>
            )}
            {canAdd(PERMISSIONS.ADD_EVENTS) && (
              <button onClick={handleBulkRegister} disabled={bulkRegistering}
                className="bg-[#C8A951] text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-[#b89740] transition whitespace-nowrap disabled:opacity-50">
                {bulkRegistering ? 'Registering...' : 'Bulk Register All'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Single Registration Form */}
      {showSingleForm && selectedEvent && (
        <div className="bg-white rounded-xl shadow-sm border p-5 mb-5">
          <h3 className="font-semibold text-gray-700 mb-4">Register Single Member</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-600 mb-1">Search Member</label>
              <input type="text" value={memberSearch} onChange={e => { setMemberSearch(e.target.value); setSelectedMember(null); }}
                placeholder="Type name or membership number..."
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
              {memberResults.length > 0 && !selectedMember && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-auto">
                  {memberResults.map(m => (
                    <button key={m.id} type="button"
                      onClick={() => {
                        setSelectedMember(m);
                        setMemberSearch(`${m.full_name || m.user_full_name} (${m.membership_number || ''})`);
                        setMemberResults([]);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm">
                      <span className="font-medium">{m.full_name || m.user_full_name}</span>
                      <span className="text-gray-400 ml-2">{m.membership_number}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-end">
              <button onClick={handleSingleRegister}
                disabled={!selectedMember || singleRegistering}
                className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition disabled:opacity-50 w-full">
                {singleRegistering ? 'Registering...' : 'Register Member'}
              </button>
            </div>
          </div>
          {selectedMember && (
            <div className="mt-3 p-3 bg-green-50 rounded-lg text-sm text-green-800">
              Selected: <strong>{selectedMember.full_name || selectedMember.user_full_name}</strong> — {selectedMember.membership_number}
            </div>
          )}
        </div>
      )}

      {!selectedEvent ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <p className="text-gray-400">Select an event to view attendances</p>
        </div>
      ) : loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : attendances.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <p className="text-gray-400">No attendances found for this event</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">S/N</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Member</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Membership #</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-600">Attendance</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-600">Certificate</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {attendances.map((a, idx) => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-500 font-medium">{idx + 1}</td>
                  <td className="px-4 py-3 font-medium text-gray-800">{a.member_name}</td>
                  <td className="px-4 py-3 text-gray-500">{a.membership_number}</td>
                  <td className="px-4 py-3 text-center">
                    {a.attendance_marked ? (
                      <span className="inline-flex items-center gap-1 text-green-600 text-xs font-semibold">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                        Marked
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">Not marked</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {a.certificate_issued ? (
                      <span className="text-green-600 text-xs font-semibold">Issued</span>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      {canEdit(PERMISSIONS.EDIT_EVENTS) && (
                        <>
                          {!a.attendance_marked ? (
                            <button onClick={() => handleAction(a.id, 'mark_attendance')}
                              className="text-xs px-3 py-1 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 font-semibold transition">
                              Mark Present
                            </button>
                          ) : (
                            <button onClick={() => handleAction(a.id, 'unmark_attendance')}
                              className="text-xs px-3 py-1 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 font-semibold transition">
                              Unmark
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
